import { memo, useContext, useState } from 'react';
import { Info, Copy, Check, ArrowRight } from 'lucide-react';
import type { AxonEvent } from '@reflex/shared';
import { cn } from '../../lib/utils';
import { formatFullTimestamp } from '../../lib/format';
import { formatPayloadRaw, buildFullMessage } from '../../client/event-utils';
import {
  EVENT_BADGE_CLASS,
  EVENT_DOT_CLASS,
  DEFAULT_EVENT_BADGE_CLASS,
  DEFAULT_EVENT_DOT_CLASS,
} from '../../client/status-colors';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { useCopyToClipboard } from '../../hooks/useCopyToClipboard';
import { useIsDesktop } from '../../hooks/useIsDesktop';
import { DetailField } from '../shared/DetailList';
import { Timestamp } from '../shared/Timestamp';
import { JsonSyntax } from '../content/log-viewer/JsonSyntax';
import { AxonViewContext } from '../agent/AxonViewContext';
import { PayloadToggle, type PayloadViewMode } from './PayloadToggle';

export interface EventDetailTooltipProps {
  event?: AxonEvent;
  onGoToEvent?: (eventId: string) => void;
}

function CopyBtn({ text }: { text: string }) {
  const { copied, copy } = useCopyToClipboard();
  return (
    <Button
      variant="ghost"
      size="icon-xs"
      onClick={(e) => {
        e.stopPropagation();
        copy(text);
      }}
      aria-label="Copy"
    >
      {copied ? <Check className="h-3 w-3 text-status-success" /> : <Copy className="h-3 w-3" />}
    </Button>
  );
}

function MetaRow({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return <DetailField label={label} value={value} mono={mono} dense />;
}

export const EventDetailTooltip = memo(function EventDetailTooltip({
  event,
  onGoToEvent,
}: EventDetailTooltipProps) {
  const [hasOpened, setHasOpened] = useState(false);
  const nav = useContext(AxonViewContext);

  if (!event) return null;

  const goToEvent = () => {
    if (onGoToEvent) {
      onGoToEvent(event.id);
    } else {
      nav.showInAxonView(event.id);
    }
  };

  return (
    <Popover
      onOpenChange={(open) => {
        if (open) setHasOpened(true);
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          onDoubleClick={(e) => {
            e.stopPropagation();
            goToEvent();
          }}
          className="text-muted-foreground hover:text-foreground transition-colors inline-flex"
          aria-label="Event details"
        >
          <Info className="h-3 w-3" />
        </button>
      </PopoverTrigger>
      {hasOpened && <EventDetailPopoverContent event={event} goToEvent={goToEvent} />}
    </Popover>
  );
});

function EventDetailPopoverContent({
  event,
  goToEvent,
}: {
  event: AxonEvent;
  goToEvent: () => void;
}) {
  const isDesktop = useIsDesktop();
  const [viewMode, setViewMode] = useState<PayloadViewMode>('payload');
  const dot = EVENT_DOT_CLASS[event.type] ?? DEFAULT_EVENT_DOT_CLASS;
  const badge = EVENT_BADGE_CLASS[event.type] ?? DEFAULT_EVENT_BADGE_CLASS;
  const raw = formatPayloadRaw(event.payload);
  const fullMsg = buildFullMessage(event);
  const fullMsgText = JSON.stringify(fullMsg, null, 2);
  const seq = event.sequence != null ? String(event.sequence) : null;

  const displayText = viewMode === 'full' ? fullMsgText : raw.text;
  const displayIsJson = viewMode === 'full' || raw.isJson;
  const displayValue = displayIsJson
    ? viewMode === 'full'
      ? fullMsg
      : JSON.parse(raw.text)
    : null;

  return (
    <PopoverContent
      side={isDesktop ? 'right' : 'bottom'}
      align={isDesktop ? 'start' : 'center'}
      collisionPadding={16}
      className="w-80 p-0 overflow-hidden"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex flex-wrap items-center gap-2 px-3 py-2.5 border-b border-border/60 bg-muted/30">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <span className={cn('h-2 w-2 rounded-full shrink-0', dot)} />
          <span
            className={cn(
              'inline-flex max-w-full min-w-0 items-center rounded-md border px-1.5 py-0.5 font-mono text-2xs font-medium',
              badge,
            )}
          >
            <span className="truncate">{event.type}</span>
          </span>
          {seq && <span className="shrink-0 text-3xs font-mono text-muted-foreground">#{seq}</span>}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <span className="text-3xs text-muted-foreground">
            {formatFullTimestamp(event.timestamp)}
          </span>
          <Button variant="ghost" size="icon-xs" onClick={goToEvent} aria-label="Go to event">
            <ArrowRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      <div className="px-3 py-2.5 space-y-1.5 border-b border-border/60">
        <MetaRow label="Event ID" value={event.id} mono />
        <MetaRow label="Stream ID" value={event.streamId} mono />
        <MetaRow label="Type" value={event.type} mono />
        {seq && <MetaRow label="Sequence" value={seq} mono />}
        <MetaRow label="Timestamp" value={formatFullTimestamp(event.timestamp)} mono />
        {event.origin && <MetaRow label="Origin" value={event.origin} mono />}
        {event.source && <MetaRow label="Source" value={event.source} mono />}
      </div>

      <div className="px-3 py-2.5">
        <div className="flex items-center justify-between mb-1.5">
          <PayloadToggle mode={viewMode} onToggle={setViewMode} />
          <div className="flex items-center gap-2">
            {displayIsJson && (
              <span className="rounded bg-status-accent-sky/10 border border-status-accent-sky/20 px-1 py-px text-3xs font-mono text-status-accent-sky">
                JSON
              </span>
            )}
            <CopyBtn text={displayText} />
          </div>
        </div>
        <pre className="rounded-md bg-muted/50 border border-border/60 p-2 text-2xs font-mono text-foreground/80 overflow-x-auto overflow-y-auto max-h-48 whitespace-pre-wrap wrap-break-word">
          {displayIsJson ? <JsonSyntax value={displayValue} /> : displayText}
        </pre>
      </div>
    </PopoverContent>
  );
}

export const EventTimestamp = memo(function EventTimestamp({ event }: { event: AxonEvent }) {
  return (
    <span className="flex items-center gap-1.5">
      <Timestamp timestamp={event.timestamp} />
      <EventDetailTooltip event={event} />
    </span>
  );
});
